import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  User as UserIcon, 
  Mail, 
  Lock, 
  Camera, 
  Save, 
  Loader2,
  Shield,
  Briefcase,
  Bell,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../App';
import { db, auth, updatePassword, updateDoc, doc } from '../firebase';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import { motion } from 'motion/react';
import { cn } from '../lib/utils';

const Profile: React.FC = () => {
  const { profile, user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    displayName: profile?.displayName || '',
    photoURL: profile?.photoURL || ''
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        photoURL: profile.photoURL || ''
      });
    }
  }, [profile]);

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB limit for base64 storage
        toast.error('Image size must be less than 1MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, photoURL: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSubmitting(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: formData.displayName,
        photoURL: formData.photoURL,
        updatedAt: Timestamp.now()
      });
      toast.success('Profile updated successfully!');
    } catch (error: any) {
      toast.error('Error updating profile: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) return;
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsPasswordSubmitting(true);
    try {
      await updatePassword(auth.currentUser, passwordData.newPassword);
      toast.success('Password updated successfully!');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Please logout and login again to change your password for security reasons.');
      } else {
        toast.error('Error updating password: ' + error.message);
      }
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Account Settings</h1>
        <p className="text-slate-500 font-medium italic">Manage your personal information and security</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="md:col-span-2 space-y-6">
          <form onSubmit={handleUpdateProfile} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-6">
            <div className="flex items-center gap-6">
              <div className="relative group">
                <div className="w-24 h-24 rounded-3xl bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-slate-50">
                  {formData.photoURL ? (
                    <img src={formData.photoURL} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <UserIcon className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <label className="absolute -bottom-2 -right-2 w-10 h-10 bg-blue-800 text-white rounded-2xl flex items-center justify-center cursor-pointer hover:bg-blue-900 transition-all shadow-lg ring-4 ring-white">
                  <Camera className="w-5 h-5" />
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                </label>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{profile?.displayName || 'User'}</h3>
                <p className="text-slate-500 text-sm font-medium">{profile?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-3 py-1 bg-blue-50 text-blue-800 text-[10px] font-black uppercase tracking-widest rounded-full">
                    {profile?.role?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Full Name</label>
                <div className="relative">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest italic">Email Address (Read Only)</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                  <input
                    type="email"
                    value={profile?.email}
                    disabled
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-400 font-bold opacity-70 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-800 text-white px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-blue-900 transition-all disabled:opacity-50 shadow-xl shadow-blue-200"
            >
              {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Profile Changes
            </button>
          </form>

          {/* Password Security Card */}
          <form onSubmit={handleUpdatePassword} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-600">
                <Lock className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Security & Password</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  placeholder="At least 6 characters"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Confirm Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  placeholder="Repeat new password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isPasswordSubmitting}
              className="w-full bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-slate-800 transition-all disabled:opacity-50"
            >
              {isPasswordSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" />}
              Update Security Password
            </button>
          </form>
        </div>

        {/* Info Sidebar */}
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-[2rem] border border-blue-100">
            <div className="flex items-center gap-3 mb-4">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <h4 className="font-bold text-blue-900">Work Info</h4>
            </div>
            <div className="space-y-4">
              <div>
                <span className="block text-[10px] font-black text-blue-400 uppercase tracking-widest">Department</span>
                <span className="text-sm font-bold text-blue-800">{profile?.department || 'Not Assigned'}</span>
              </div>
              <div>
                <span className="block text-[10px] font-black text-blue-400 uppercase tracking-widest">Employee ID</span>
                <span className="text-sm font-bold text-blue-800 uppercase">{profile?.uid.substring(0, 8)}</span>
              </div>
              <div>
                <span className="block text-[10px] font-black text-blue-400 uppercase tracking-widest">Member Since</span>
                <span className="text-sm font-bold text-blue-800">{profile?.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
              </div>
            </div>
          </div>

          <Link to="/user-settings" className="block bg-gradient-to-br from-blue-900 to-indigo-950 p-6 rounded-[2rem] text-white hover:shadow-xl transition-all group relative overflow-hidden">
            <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-10">
              <Bell className="w-32 h-32" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1">
                <h4 className="font-bold text-sm tracking-tight flex items-center gap-1.5 text-blue-100">
                  <Bell className="w-4 h-4 text-emerald-400 group-hover:animate-bounce" />
                  Alert Preferences
                </h4>
                <p className="text-[11px] text-white/75 leading-relaxed font-semibold">
                  Customize Email & In-App delivery routing settings
                </p>
              </div>
              <ChevronRight className="w-5 h-5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-emerald-400 shrink-0" />
            </div>
          </Link>

          <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
            <h4 className="font-bold text-slate-900 mb-2">Privacy Note</h4>
            <p className="text-xs text-slate-500 leading-relaxed font-medium">
              Your email address is managed by the system administrator and cannot be changed. If you need to update your official email, please contact IT support.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
